import { cn } from '@/lib/utils'
import { Play, Video } from 'lucide-react'

export interface Module {
  id: string
  title: string
  thumbnail: string
  duration: string
  moduleNumber: number
  isCompleted?: boolean
}

export interface LearningPathProps {
  modulesCompleted?: number
  totalModules?: number
  modules?: Module[]
  onViewAll?: () => void
  onModuleClick?: (moduleId: string) => void
  className?: string
}

export function LearningPath({
  modulesCompleted = 2,
  totalModules = 4,
  modules = [],
  onViewAll,
  onModuleClick,
  className,
}: LearningPathProps) {
  // Show max 2 modules on mobile, 3 on larger screens
  const displayModules = modules.slice(0, 3)

  return (
    <div className={cn('rounded-[24px] bg-white p-[34px] w-full min-h-[487px]', className)}>
      {/* Header */}
      <div className='mb-[4px] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <h2 className='text-[32px] font-bold text-[#151515] leading-[70px]'>
          Learning Path
        </h2>
        <div className='rounded-full border border-[#434343] px-4 py-2 h-[40px] flex items-center justify-center w-fit'>
          <span className='text-[12px] font-normal text-[#434343]'>
            {modulesCompleted}/{totalModules} modules completed, keep going!
          </span>
        </div>
      </div>

      <p className='text-[16px] text-[#4e4d4d] font-normal leading-[18px] mb-8'>
        These modules are designed to help you grow together.
      </p>

      {/* Module Cards */}
      <div className='mb-8 flex gap-4 overflow-x-auto pb-2'>
        {displayModules.map(module => (
          <button
            key={module.id}
            onClick={() => onModuleClick?.(module.id)}
            className='group relative overflow-hidden text-left transition-transform hover:scale-[1.02] flex-shrink-0 w-[265px]'
          >
            {/* Thumbnail */}
            <div className='relative h-[164px] overflow-hidden rounded-[12px] bg-[#edeff3]'>
              <img
                src={module.thumbnail || '/placeholder.svg'}
                alt={module.title}
                className='object-cover w-full h-full'
              />

              {/* Gradient Overlay */}
              <div
                className='absolute inset-0'
                style={{ background: 'linear-gradient(180deg, rgba(255, 102, 0, 0.02) 0%, rgba(0, 0, 0, 0.7) 100%)' }}
              />

              {/* Completed Badge */}
              {module.isCompleted && (
                <div className='absolute right-[23px] top-[7px]'>
                  <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                    <circle cx='12' cy='12' r='12' fill='#0ECF5B' />
                    <path d='M8 12l3 3 5-6' stroke='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                  </svg>
                </div>
              )}

              {/* Play Button Overlay */}
              <div className='absolute inset-0 flex items-center justify-center'>
                <div className='flex h-[46px] w-[46px] items-center justify-center rounded-full bg-white shadow-lg transition-transform group-hover:scale-110'>
                  <Play
                    className='ml-1 h-5 w-5 text-[#151515]'
                    fill='currentColor'
                  />
                </div>
              </div>

              {/* Duration Badge */}
              <div className='absolute bottom-[43px] right-[34px] rounded-[6px] bg-white/20 px-[10px] py-[10px] h-[24px] flex items-center justify-center'>
                <span className='text-[12px] font-bold text-white leading-[32px]'>
                  {module.duration}
                </span>
              </div>
            </div>

            {/* Module Label */}
            <div className='mt-3'>
              <div className='bg-[#151515] rounded-[5px] px-2 py-1 flex items-center gap-1 w-fit'>
                <Video className='h-[15px] w-[15px] text-white' />
                <span className='text-[12px] font-bold text-white leading-[16px]'>
                  Module {module.moduleNumber}
                </span>
              </div>
            </div>

            {/* Module Title */}
            <div className='mt-1.5'>
              <h3 className='text-[16px] font-bold text-[#151515] leading-[20px] line-clamp-2'>{module.title}</h3>
            </div>
          </button>
        ))}
      </div>

      {/* View All Button */}
      <div className='flex justify-center'>
        <button
          onClick={onViewAll}
          className='h-[44px] rounded-full bg-[#151515] px-[14px] py-[10px] text-[16px] font-bold text-white hover:bg-[#2a2a2a] transition-colors'
        >
          View All Modules
        </button>
      </div>
    </div>
  )
}
